import React, { Fragment, Component } from 'react';
import NukaCarousel from 'nuka-carousel';
import PropTypes from 'prop-types';
import { LoadingSpinner } from 'legacy-packages/ft-cb';

import './Carousel.scss';

const ITEM_TYPES = {
  PINNED: 'pinnedItems',
  CAROUSEL: 'items',
};

/**
 * @classdesc Multi-purpose, responsive carousel based on nuka-carousel.
 * @see https://github.com/FormidableLabs/nuka-carousel for module options.
 * @param {object} settings Configuration object for nuka-carousel.
 * @param {array} pinnedItems Allows for one or more results to be "pinned" outside the carousel.
 *                            Only up to (2) items will be displayed.
 *
 * @param {array} items Items to display in the carousel.
 * @param {string|number|array|object} noItems Content to display given no items or pinnedItems.
 * @param {object} containerAttributes Optional props to pass to the utmost containing element.
 * @param {string} prevContainerClass Class(es) for a custom previous button containing element.
 * @param {string} nextContainerClass Class(es) for a custom next button containing element.
 * @param {number} maxCardsOnPage The number of cards the carousel will display.
 * @param {number} fitCards The boolean for knowing if maxCardsOnPage fit the screen.
 */
class Carousel extends Component {
  static propTypes = {
    settings: PropTypes.object,
    pinnedItems: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.node,
        PropTypes.string,
        PropTypes.number,
        PropTypes.object,
      ]),
    ),
    items: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.node,
        PropTypes.string,
        PropTypes.number,
        PropTypes.object,
      ]),
    ).isRequired,
    noItems: PropTypes.oneOfType([
      PropTypes.node,
      PropTypes.string,
      PropTypes.number,
      PropTypes.element,
    ]),
    containerAttributes: PropTypes.object,
    prevContainerClass: PropTypes.string,
    nextContainerClass: PropTypes.string,
    maxCardsOnPage: PropTypes.number,
    fitCards: PropTypes.bool,
  };

  static defaultProps = {
    settings: {
      dots: false,
      infinite: true,
      speed: 500,
      slidesToScroll: 1,
      prevArrow: null,
      nextArrow: null,
      prevButton: null,
      nextButton: null,
      skipPrevButtons: null,
      skipNextButtons: null,
      variableWidth: true,
    },
    pinnedItems: [],
    items: ['Test1', 'Test2', 'Test3', 'Test4'],
    noItems: '',
    containerAttributes: {},
    prevContainerClass: 'btn-prev',
    nextContainerClass: 'btn-next',
    maxCardsOnPage: 0,
    fitCards: false,
  };

  constructor(props) {
    super(props);

    const carouselItems = props[ITEM_TYPES.CAROUSEL];
    this.totalItems = carouselItems ? carouselItems.length : 0;

    this.renderItems = this.renderItems.bind(this);

    const { maxCardsOnPage, fitCards } = this.props;

    this.state = {
      maxCardsOnPage,
      containerAttributes: {},
      fitCards,
    };
  }

  /**
   * @param {string} [which = 'items'] Specifies which collection of items in state to subrender.
   *                                   Defaults to "items".
   */
  renderItems(which = ITEM_TYPES.CAROUSEL) {
    const items = this.props[which];

    const itemContent = (props) => {
      const { which, index, item } = props;

      return (
        <div
          key={`${which}-item-${index}`}
          className={`${which}-slide ${item.className ? item.className : ''}`}
        >
          {item}
        </div>
      );
    };

    /**
     * For carousel items when there is only one item
     * render item as list
     * */
    if (which === ITEM_TYPES.CAROUSEL && items.length === 1) {
      return (
        <li className="slider-slide">
          {itemContent({
            item: items[0],
            index: 0,
            which,
          })}
        </li>
      );
    }
    return items.map((item, index) =>
      itemContent({
        item,
        index,
        which,
      }),
    );
  }

  shouldComponentUpdate(nextProps) {
    return nextProps.maxCardsOnPage;
  }

  componentWillReceiveProps(nextProps) {
    const { maxCardsOnPage, fitCards } = nextProps;
    if (maxCardsOnPage) {
      this.setState({
        maxCardsOnPage,
        fitCards,
      });
    }
  }

  componentWillMount() {
    const { pinnedItems, containerAttributes } = this.props;
    containerAttributes.className = `${
      containerAttributes.className
        ? `carousel-widget-container ${
            !pinnedItems.length ? 'full-width' : ''
          } ${containerAttributes.className}`
        : containerAttributes.className
    } page_wrapper`;
    this.setState({
      containerAttributes,
    });
  }

  render() {
    const { containerAttributes, maxCardsOnPage, fitCards } = this.state;
    const {
      settings,
      pinnedItems,
      noItems,
      items,
      prevContainerClass,
      nextContainerClass,
      carouselTitle,
    } = this.props;
    const {
      prevArrow,
      nextArrow,
      prevButton,
      nextButton,
      skipPrevButtons,
      skipNextButtons,
      slideIndex,
    } = settings;

    /**
     * Show decorator only if are enough cards per page
     * or if the cards are not fitting the screen
     * */
    let showDecorators = maxCardsOnPage < this.totalItems;

    if (!showDecorators && !fitCards) {
      showDecorators = true;
    }

    const carouselProps = {
      settings: {
        slideIndex,
        slidesToShow: maxCardsOnPage,
        wrapAround: false,
        slideWidth: '226px',
        decorators: [
          {
            component: (componentProps) => {
              const _props = {
                maxCardsOnPage,
                skipPrevButtons,
                ...componentProps,
              };
              return showDecorators ? <SkipPrevButtons {..._props} /> : null;
            },
          },
          {
            component: (componentProps) => {
              const _props = {
                prevContainerClass,
                prevArrow,
                prevButton,
                fitCards,
                ...componentProps,
              };
              return showDecorators ? <PrevButton {..._props} /> : null;
            },
          },
          {
            component: (componentProps) => {
              const _props = {
                nextContainerClass,
                nextArrow,
                nextButton,
                maxCardsOnPage,
                fitCards,
                ...componentProps,
              };
              return showDecorators ? <NextButton {..._props} /> : null;
            },
          },
          {
            component: (componentProps) => {
              const _props = {
                maxCardsOnPage,
                skipNextButtons,
                ...componentProps,
              };
              return showDecorators ? <SkipNextButtons {..._props} /> : null;
            },
          },
        ],
      },
    };

    const changeFocusOnButtons = (currentSlide) => {
      const maxSlideToGo = this.carousel.state.slideCount - maxCardsOnPage + 1;

      const focusElementWithTimeoutById = (id) => {
        setTimeout(() => {
          document.getElementById(id).focus();
        }, 0);
      };

      if (currentSlide === 0) {
        focusElementWithTimeoutById('next-todo-card-button');
        return;
      }
      if (currentSlide === maxSlideToGo) {
        focusElementWithTimeoutById('prev-todo-card-button');
      }
    };

    return maxCardsOnPage ? (
      <section {...containerAttributes}>
        {!!pinnedItems.length && (
          <div className="pinned-container">
            {this.renderItems(ITEM_TYPES.PINNED)}
          </div>
        )}
        <div className="carousel-parent-container">
          <div
            className={`relative carousel-container ${
              noItems && !items.length ? 'no-items' : ''
            }`}
          >
            {!!items.length ? (
              <Fragment>
                {carouselTitle}
                <NukaCarousel
                  ref={(node) => (this.carousel = node)}
                  {...carouselProps.settings}
                  frameOverflow="visible"
                  afterSlide={changeFocusOnButtons}
                >
                  {this.renderItems(ITEM_TYPES.CAROUSEL)}
                </NukaCarousel>
              </Fragment>
            ) : (
              noItems
            )}
          </div>
        </div>
      </section>
    ) : (
      <section {...containerAttributes}>
        <LoadingSpinner />
      </section>
    );
  }
}

const triggerClickOnEnter = (callback) => (event) => {
  if (event.keyCode === 13) {
    callback();
  }
};

const PrevButton = (props) => {
  const {
    prevArrow,
    currentSlide,
    prevContainerClass,
    previousSlide,
    prevButton,
  } = props;
  const isDisabled = currentSlide === 0;

  if (prevButton) {
    return React.cloneElement(prevButton, {
      disabled: isDisabled,
      onClick: previousSlide,
    });
  }

  return (
    <span
      role="button"
      tabIndex={-1}
      aria-label="Previous"
      className={`default-carousel-prev ${
        isDisabled ? 'is-disabled ' : ''
      }${prevContainerClass}`}
      onClick={!isDisabled ? previousSlide : undefined}
      onKeyDown={!isDisabled ? triggerClickOnEnter(previousSlide) : undefined}
    >
      {prevArrow}
    </span>
  );
};

const NextButton = (props) => {
  const {
    slideCount,
    maxCardsOnPage,
    slidesToScroll,
    nextArrow,
    currentSlide,
    nextContainerClass,
    nextSlide,
    fitCards,
    nextButton,
    goToSlide,
  } = props;
  const shownCards = currentSlide + slidesToScroll + maxCardsOnPage;
  let isDisabled = shownCards > slideCount;

  let customNextSlide = (!isDisabled && nextSlide) || undefined;
  if (isDisabled && !fitCards && shownCards - slideCount === 1) {
    isDisabled = false;
    customNextSlide = () => {
      /**
       * If last card doesn't fit in the screen slide it in
       * */
      goToSlide(currentSlide + 1);
    };
  }

  if (nextButton) {
    return React.cloneElement(nextButton, {
      disabled: isDisabled,
      onClick: customNextSlide,
    });
  }

  return (
    <span
      role="button"
      tabIndex={-1}
      aria-label="Next"
      className={`default-carousel-next ${
        isDisabled ? 'is-disabled ' : ''
      }${nextContainerClass}`}
      onClick={customNextSlide}
      onKeyDown={!isDisabled ? triggerClickOnEnter(customNextSlide) : undefined}
    >
      {nextArrow}
    </span>
  );
};

const SkipPrevButtons = (props) => {
  const {
    skipPrevButtons,
    maxCardsOnPage,
    currentSlide,
    goToSlide,
    slideCount,
  } = props;

  if (skipPrevButtons) {
    return React.cloneElement(skipPrevButtons, {
      maxCardsOnPage,
      currentSlide,
      goToSlide,
      slideCount,
    });
  }

  return null;
};

const SkipNextButtons = (props) => {
  const {
    skipNextButtons,
    maxCardsOnPage,
    currentSlide,
    goToSlide,
    slideCount,
  } = props;

  if (skipNextButtons) {
    return React.cloneElement(skipNextButtons, {
      maxCardsOnPage,
      currentSlide,
      goToSlide,
      slideCount,
    });
  }

  return null;
};

export default Carousel;
